import moment from "moment";

export function isTodayOrFuture(dateStr: string): boolean {
  return moment(dateStr).startOf("day").diff(moment().startOf("day"), "days") >= 0;
}

export function isFutureDay(dateStr: string): boolean {
  return moment(dateStr).startOf("day").isAfter(moment().startOf("day"));
}

export function formatMetricNumber(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function shiftDate(dateStr: string, offsetDays: number): string {
  return moment(dateStr)
    .add(offsetDays, "day")
    .format("YYYY-MM-DD");
}

/** Sauce: output → next opening. Cheese: output → next "плюс выпуск". */
export function outputToOpening(forecastGroup: string): boolean {
  return forecastGroup === "sauce";
}

/** Tab cheese/sauce forces mode; tab general uses each product's setting. */
export function resolveOutputGroup(
  tabGroup: string,
  productGroup?: string | null
): string {
  if (tabGroup === "general") {
    return productGroup === "sauce" ? "sauce" : "cheese";
  }
  return tabGroup === "sauce" ? "sauce" : "cheese";
}

export function outputSumForDay(dayData: any | undefined, slots: any[]): number {
  if (!dayData) {
    return 0;
  }
  return slots.reduce((sum: number, slot: any) => {
    return sum + formatMetricNumber(dayData.slot_values?.[slot.key]?.quantity);
  }, 0);
}

export function recalcPlusOutput(
  dayData: any,
  _slots: any[],
  prevOutputSum: number = 0,
  forecastGroup: string = "cheese"
) {
  const plan = formatMetricNumber(dayData.close_plan);
  dayData.close_with_output = outputToOpening(forecastGroup)
    ? plan
    : plan + prevOutputSum;
}

/**
 * Opening of the next day: fact (1C upload) wins over plan carry —
 * cheese: fact as is; sauce: fact + same-day output (arrives next morning).
 * Without a fact — sauce: close_plan + output; cheese: close_with_output.
 */
export function nextDayOpeningFromPrev(
  prevDayData: any,
  slots: any[],
  forecastGroup: string = "cheese"
): number {
  if (prevDayData?.fact_enabled) {
    const fact = formatMetricNumber(prevDayData.close_fact);
    return outputToOpening(forecastGroup)
      ? fact + outputSumForDay(prevDayData, slots)
      : fact;
  }
  if (outputToOpening(forecastGroup)) {
    return (
      formatMetricNumber(prevDayData.close_plan) +
      outputSumForDay(prevDayData, slots)
    );
  }
  return formatMetricNumber(prevDayData.close_with_output);
}

export function recalcDayFromOpening(
  dayData: any,
  slots: any[] = [],
  prevOutputSum: number = 0,
  forecastGroup: string = "cheese"
) {
  const opening = formatMetricNumber(dayData.opening_balance);
  const z = formatMetricNumber(dayData.shipment_deal);
  const p = formatMetricNumber(dayData.shipment_movement);
  dayData.close_plan = opening - z - p;
  recalcPlusOutput(dayData, slots, prevOutputSum, forecastGroup);
}

/** Recompute close_with_output for cheese/general using previous day's output. */
export function recalcDayOutputShift(
  days: { date: string }[],
  rowDays: Record<string, any>,
  date: string,
  slots: any[],
  forecastGroup: string = "cheese"
) {
  if (outputToOpening(forecastGroup)) {
    return;
  }

  const dayData = rowDays[date];
  if (!dayData) {
    return;
  }

  const dayDates = days.map((day) => day.date);
  const idx = dayDates.indexOf(date);
  const prevDate = idx > 0 ? dayDates[idx - 1] : shiftDate(date, -1);
  const prevOutputSum = outputSumForDay(rowDays[prevDate], slots);
  recalcPlusOutput(dayData, slots, prevOutputSum, forecastGroup);
}

export function rollForwardProductDays(
  days: { date: string }[],
  rowDays: Record<string, any>,
  changedDate: string,
  slots: any[],
  forecastGroup: string = "cheese"
): Record<string, any> {
  if (!isTodayOrFuture(changedDate)) {
    return rowDays;
  }

  const dayDates = days.map((day) => day.date);
  const startIdx = dayDates.indexOf(changedDate);
  if (startIdx < 0) {
    return rowDays;
  }

  const nextDays = { ...rowDays };

  for (let i = startIdx + 1; i < dayDates.length; i++) {
    const date = dayDates[i];
    if (!isFutureDay(date)) {
      continue;
    }

    const prevDate = dayDates[i - 1];
    const prevDayData = nextDays[prevDate];
    if (!prevDayData) {
      continue;
    }

    const prevOutputSum = outputSumForDay(prevDayData, slots);
    const dayData = { ...(nextDays[date] || {}) };
    dayData.opening_balance = nextDayOpeningFromPrev(
      prevDayData,
      slots,
      forecastGroup
    );
    recalcDayFromOpening(dayData, slots, prevOutputSum, forecastGroup);
    nextDays[date] = dayData;
  }

  return nextDays;
}
